/** Quick Search — loose text → structured stock filters (Mill Command Center plan, §F).
 *
 *  Reuses Quick Tally's exact fuzzy grammar (species/origin/quality — same Levenshtein-based
 *  fuzzyFind, same "flag every fuzzy hit, never silently guess" discipline) rather than a second
 *  implementation. Species is the one thing Quick Tally never had to solve (a tally sheet is
 *  already scoped to one product) — Product.name is unconstrained free text, so it's matched with
 *  fuse.js (FuzzyIndex, already used by the bulk-import dialog) against the tenant's own distinct
 *  product list, not a fixed vocabulary.
 *
 *  Dimension grammar (comparator search — girth > 90cm, width between 6 and 10 inch): a
 *  comparator (>, <, over, under, at least, at most, min, max, between X and Y) must be attached
 *  to an explicit field keyword (girth/length/width/thick(ness)) AND carry an explicit unit —
 *  position never implies which field a lone comparator+number belongs to, and a unit is never
 *  assumed when a comparator is used.
 *
 *  Two everyday shortcuts are handled with transparency instead of outright refusal, learned
 *  directly from real testing (a plain "4x3x6" or "200mm" returning zero filtering with no
 *  explanation felt broken, not safe):
 *  - Positional shorthand ("4x3x6", "90x230") — 2 numbers = Round girth×length, 3 = Square
 *    width×thickness×length, same structure Quick Tally itself uses. If every number carries its
 *    own unit, it's an exact match, no assumption involved. If units are omitted, the tenant's own
 *    configured default UoM (Application Settings → Tally Defaults) is assumed instead of a
 *    hardcoded guess — and the chip visibly marks it as assumed (same "?" fuzzy-flag icon already
 *    used for uncertain species/origin matches) so a wrong assumption is obvious, not invisible.
 *  - Wildcard shorthand ("4x3x?", "4x?x6", "90x?") — a "?" leaves that one dimension completely
 *    open ("any 4x3, whatever the length"). It is NOT the same as just dropping the number:
 *    "4x3" already means something else (Round girth×length), so the slot has to be held by a
 *    real character. "?" was picked because "*" is already a separator alias here ([x×*], so
 *    "4*3" = 4 by 3) and "x"/"X" is the separator itself. A wildcard slot pushes no constraint
 *    at all (an open slot must not filter); it shows up as a "length: any" chip so it's visible
 *    that the wildcard was understood. A 3-slot pattern still means Square even when a slot is
 *    a wildcard — that is the whole reason "4x3x?" beats "4x3" for a Square search. An
 *    all-wildcard pattern ("?x?x?") is rejected as a size, since it would filter nothing.
 *    A lone "x"/"X" standing in a slot position ("4x6xX") is accepted as an alias for "?" —
 *    that's the shape people reach for first — but "?" stays the documented/suggested form,
 *    because "X" next to the "x" separator is hard to read back.
 *  - A lone number+unit with no field word ("200mm") is never silently dropped — it becomes an
 *    "ambiguous size" the caller resolves using real signal (the matched product's own Round/
 *    Square type when known) rather than an invented plausibility range; see resolveAmbiguousSizes
 *    in quick-search.service.ts.
 *
 *  A word that matches nothing at all (species/origin/quality/stopword) is flagged back to the
 *  user instead of silently vanishing — "maninagar" not appearing anywhere used to look identical
 *  to a correct zero-result search.
 */

import { fuzzyFind, normalize } from
  '../../modules/new-tallysheet/components/tallysheet-square-list/quick-tally-parser';
import { FuzzyIndex, FuzzyCandidate } from '../components/bulk-import-dialog/fuzzy-match';

export interface QuickSearchCountry { name: string; countryCode: string; }
export interface QuickSearchGrade { code: string; label: string; }
export interface QuickSearchProduct { id: number; name: string; isRound: boolean | null; }

/** Tenant's own configured measurement defaults (Application Settings → Tally Defaults) — the
 *  source both the unitless-shorthand assumption and the tolerance band read from. Null fields
 *  fall back to today's literal (Inch/Inch/Ft square, CM/CM round, 5%/8% tolerance) — the exact
 *  same values TallyDefaultSettingsServiceImpl.defaults() seeds a brand-new tenant with. */
export interface QuickSearchTenantDefaults {
  widthUom: string | null;
  thickUom: string | null;
  squareLengthUom: string | null;
  girthUom: string | null;
  roundLengthUom: string | null;
  squareSearchTolerancePercent: number | null;
  roundSearchTolerancePercent: number | null;
}

export interface QuickSearchMatch {
  field: 'dimensions' | 'type' | 'species' | 'species-ambiguous' | 'origin' | 'quality' | 'unrecognized';
  label: string;
  fuzzy: boolean;
}

export type DimensionField = 'width' | 'thickness' | 'length' | 'girth';

/** One resolved dimension constraint — the comparator has already been folded into [minMm,maxMm]
 *  by the time this exists (eq: min=max; gt/gte: min only; lt/lte: max only; between: both).
 *  assumed=true means the unit wasn't typed — it came from the tenant's configured default. */
export interface DimensionConstraint {
  field: DimensionField;
  minMm?: number;
  maxMm?: number;
  label: string;
  assumed: boolean;
}

/** A lone number+unit with no field word ("200mm") — real, not dropped, but which physical
 *  measurement it refers to is still unresolved at parse time. quick-search.service.ts resolves
 *  it using the matched product's own type when known, or checks all plausible fields otherwise. */
export interface AmbiguousSize {
  valueMm: number;
  rawValue: string;
  unitLabel: string;
}

/** A typed word that fuzzy-matched a product name closely enough to be plausible, but not closely
 *  enough to auto-pick (score between SPECIES_STRONG_THRESHOLD and fuse.js's own configured 0.4
 *  cutoff) — surfaced as a "Did you mean?" pick-list instead of guessing. Real report, 2026-08-19:
 *  typing "tick" (a typo for "teak") silently auto-picked an unrelated product ("PRD Quick 1")
 *  because it happened to score closer under fuse.js's algorithm — auto-picking anything under a
 *  loose blanket threshold was itself the bug. */
export interface AmbiguousSpecies {
  word: string;
  candidates: { id: number; name: string }[];
}

export interface QuickSearchQuery {
  raw: string;
  /** null when nothing pinned it to one type — a pure species/origin/quality lookup, a bare
   *  length constraint (Square-only, a deliberate scope limit — see the module doc), or an
   *  ambiguous size with no product-type signal yet. true/false once a girth or width/thickness
   *  constraint exists, or once resolveAmbiguousSizes() pins it down via product type. */
  isRound: boolean | null;
  dimensionConstraints: DimensionConstraint[];
  /** Dimension slots the user deliberately left open with the "?" wildcard ("4x3x?" = any
   *  length). Carried separately from dimensionConstraints on purpose: a wildcard is the
   *  ABSENCE of a filter, so it must never become a constraint object — it exists here only so
   *  the chip row can show "length: any" and prove the wildcard was understood. */
  wildcardDimensions: DimensionField[];
  ambiguousSizes: AmbiguousSize[];
  /** Words that fuzzy-matched a product closely enough to be plausible but not closely enough to
   *  auto-pick — see AmbiguousSpecies. Almost always 0 or 1 entries (productId locks in on the
   *  first accepted match), kept as an array purely for symmetry with ambiguousSizes. */
  ambiguousSpecies: AmbiguousSpecies[];
  originCode?: string;
  originName?: string;
  qualityCode?: string;
  qualityLabel?: string;
  productId?: number;
  productName?: string;
  /** Set once productId resolves and that product's own type is known — the strongest signal
   *  for resolving ambiguousSizes, no guessing required. */
  productIsRound?: boolean | null;
  matches: QuickSearchMatch[];
}

const MM_PER_UNIT: Record<string, number> = {
  cm: 10, mm: 1,
  in: 25.4, inch: 25.4, inches: 25.4, '"': 25.4,
  ft: 304.8, feet: 304.8, "'": 304.8,
};

/** Maps a tenant's stored UoM string (free text today — "Inch", "CM", "Ft", "MM" — see
 *  TallyDefaultSettingsServiceImpl) to the mm-per-unit table above. */
function tenantUnitToMm(uom: string | null | undefined, fallback: string): number {
  const key = (uom || fallback).toLowerCase().replace(/e?s?$/, '');
  return MM_PER_UNIT[key] ?? MM_PER_UNIT[fallback.toLowerCase()] ?? 1;
}
function tenantUnitLabel(uom: string | null | undefined, fallback: string): string {
  return uom || fallback;
}

// A fuse.js score below this is treated as confident enough to auto-pick, same as today's
// behavior. Between this and fuse.js's own configured 0.4 threshold (see FuzzyIndex), the match
// is plausible but not certain — surfaced as a "Did you mean?" pick-list instead of guessed.
const SPECIES_STRONG_THRESHOLD = 0.2;

/** The "leave this dimension open" wildcard — "4x3x?" = width 4, thickness 3, ANY length.
 *  Deliberately NOT "*" (already a separator alias, see the [x×*] class below) and not "x"/"X"
 *  (that IS the separator). See the wildcard branch in extractDimensionConstraints(). */
const WILDCARD = '?';

const UNIT_RE = `(cm|mm|in(?:ch(?:es)?)?|"|ft|feet|')`;
const FIELD_RE = `(girth|length|width|thick(?:ness)?)`;
const CMP_SYMBOL_RE = `(>=|<=|>|<)`;
const CMP_WORD_RE = `(over|above|under|below|at\\s+least|at\\s+most|min(?:imum)?|max(?:imum)?)`;

// Common filler/connector words a natural sentence carries that aren't meant to be search terms
// — silently ignored rather than flagged as "unrecognized" (which would make ordinary phrasing
// like "show me any teak available" look full of errors). Deliberately not exhaustive; the goal
// is cutting noise, not formal NLP.
const STOPWORDS = new Set([
  'a', 'an', 'the', 'of', 'in', 'is', 'are', 'any', 'some', 'show', 'me', 'us', 'got', 'have',
  'has', 'do', 'you', 'we', 'i', 'looking', 'for', 'available', 'availability', 'stock', 'stocks',
  'please', 'pls', 'need', 'want', 'find', 'search', 'get', 'and', 'or', 'with', 'to', 'from',
  'at', 'on', 'that', 'this', 'there', 'anything', 'something', 'if', 'can', 'could', 'would',
]);

function unitToMm(raw: string): number {
  return MM_PER_UNIT[raw.toLowerCase()] ?? 1;
}

function normalizeField(raw: string): DimensionField {
  const f = raw.toLowerCase();
  if (f.startsWith('thick')) return 'thickness';
  return f as DimensionField;
}

function comparatorType(symbol: string | undefined, word: string | undefined): 'eq' | 'gt' | 'gte' | 'lt' | 'lte' {
  const token = (symbol || word || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (token === '>' || token === 'over' || token === 'above') return 'gt';
  if (token === '<' || token === 'under' || token === 'below') return 'lt';
  if (token === '>=' || token === 'at least' || token.startsWith('min')) return 'gte';
  if (token === '<=' || token === 'at most' || token.startsWith('max')) return 'lte';
  return 'eq';
}

function toConstraint(field: DimensionField, valueMm: number, cmp: 'eq' | 'gt' | 'gte' | 'lt' | 'lte',
                       unitLabel: string, rawValue: string, assumed = false): DimensionConstraint {
  const base = { field, label: '', assumed };
  const suffix = assumed ? ' ?' : '';
  switch (cmp) {
    case 'gt': case 'gte':
      return { ...base, minMm: valueMm, label: `${field} > ${rawValue}${unitLabel}${suffix}` };
    case 'lt': case 'lte':
      return { ...base, maxMm: valueMm, label: `${field} < ${rawValue}${unitLabel}${suffix}` };
    default:
      return { ...base, minMm: valueMm, maxMm: valueMm, label: `${field} ${rawValue}${unitLabel}${suffix}` };
  }
}

// Hand-picked alias list, NOT the generic Levenshtein fuzzyFind used elsewhere in this file — a
// real collision was found while building this: "rough" (as in the real product "Teakwood Rough
// Square") sits only 2 edits from "round", well inside fuzzyFind's own tolerance for a 5-letter
// word. A general distance check would silently eat "rough" out of a species search and treat it
// as a Round type-hint — exactly backwards for a product whose name literally contains "Square".
// A short closed list only recognizes typos of "square"/"round" themselves, so a real species word
// that merely happens to be nearby in spelling is never at risk.
const SQUARE_ALIASES = new Set(['square', 'sqr', 'sqre', 'sqare', 'sqar', 'squre', 'squar', 'sqaure', 'sq']);
const ROUND_ALIASES = new Set(['round', 'rnd', 'rond', 'raund', 'rount', 'rnud', 'rund', 'rownd', 'rd']);

/** Explicit "square"/"round" keyword — the only way to tell a bare 2-number shorthand ("4x3")
 *  apart between Square width×thickness and Round girth×length, since the numbers alone can't say
 *  which. Real report, 2026-08-19: "4x3" defaulted to Round (girth 4cm/length 3cm, an unrealistic
 *  size) when the user meant a 4"×3" Square piece. Scanned and stripped from the text BEFORE
 *  dimension extraction so the shorthand branch below can use it. Never needed when a field word
 *  is already explicit ("girth 90cm") — only bare shorthand is actually ambiguous. */
function extractTypeHint(text: string): { isRound: boolean | null; label: string | null; exact: boolean; rest: string } {
  const words = text.split(/\s+/);
  for (let i = 0; i < words.length; i++) {
    const w = words[i].replace(/[^\p{L}]/gu, '').toLowerCase();
    if (!w) continue;
    const isSquare = SQUARE_ALIASES.has(w);
    const isRoundWord = !isSquare && ROUND_ALIASES.has(w);
    if (isSquare || isRoundWord) {
      const withoutWord = [...words];
      withoutWord[i] = ' '.repeat(words[i].length);
      return {
        isRound: isRoundWord, label: isSquare ? 'Square' : 'Round',
        exact: w === 'square' || w === 'round', rest: withoutWord.join(' '),
      };
    }
  }
  return { isRound: null, label: null, exact: true, rest: text };
}

/** Extracts every dimension constraint from the text, mutating nothing — returns the constraints
 *  found, any ambiguous (field-less) size mentions, and the leftover text (matched spans blanked
 *  out) for the species/origin/quality pass that runs afterward. typeHint (from extractTypeHint)
 *  disambiguates a bare 2-number shorthand between Square and Round when explicitly stated. */
function extractDimensionConstraints(
  text: string,
  tenantDefaults?: QuickSearchTenantDefaults | null,
  typeHint: boolean | null = null,
): { constraints: DimensionConstraint[]; wildcards: DimensionField[]; squareShorthand: boolean; ambiguous: AmbiguousSize[]; rest: string } {
  const constraints: DimensionConstraint[] = [];
  const wildcards: DimensionField[] = [];
  let squareShorthand = false;
  let rest = text;

  const blank = (match: string) => ' '.repeat(match.length);

  // ── "between X and Y" — field before or after ─────────────────────────────
  const betweenFieldFirst = new RegExp(
    `\\b${FIELD_RE}\\s+between\\s+(\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}?\\s+and\\s+(\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}?\\b`, 'gi');
  const betweenFieldAfter = new RegExp(
    `\\bbetween\\s+(\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}?\\s+and\\s+(\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}?\\s+${FIELD_RE}\\b`, 'gi');

  rest = rest.replace(betweenFieldFirst, (m, field, v1, u1, v2, u2) => {
    const unit = u1 || u2;
    if (!unit) return m; // no unit anywhere in the clause — reject per "require unit for ranges"
    const mm1 = parseFloat(v1) * unitToMm(unit);
    const mm2 = parseFloat(v2) * unitToMm(unit);
    constraints.push({
      field: normalizeField(field), minMm: Math.min(mm1, mm2), maxMm: Math.max(mm1, mm2), assumed: false,
      label: `${normalizeField(field)} ${v1}-${v2}${unit}`,
    });
    return blank(m);
  });
  rest = rest.replace(betweenFieldAfter, (m, v1, u1, v2, u2, field) => {
    const unit = u1 || u2;
    if (!unit) return m;
    const mm1 = parseFloat(v1) * unitToMm(unit);
    const mm2 = parseFloat(v2) * unitToMm(unit);
    constraints.push({
      field: normalizeField(field), minMm: Math.min(mm1, mm2), maxMm: Math.max(mm1, mm2), assumed: false,
      label: `${normalizeField(field)} ${v1}-${v2}${unit}`,
    });
    return blank(m);
  });

  // ── field, then optional comparator, then number+unit ("girth over 90cm", "girth 90cm") ──
  const fieldFirst = new RegExp(
    `\\b${FIELD_RE}\\s+(?:${CMP_SYMBOL_RE}|${CMP_WORD_RE})?\\s*(\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}\\b`, 'gi');
  rest = rest.replace(fieldFirst, (m, field, sym, word, val, unit) => {
    const cmp = comparatorType(sym, word);
    const mm = parseFloat(val) * unitToMm(unit);
    constraints.push(toConstraint(normalizeField(field), mm, cmp, unit, val));
    return blank(m);
  });

  // ── comparator, number+unit, then field ("over 90cm girth") ───────────────
  const cmpFirst = new RegExp(
    `\\b(?:${CMP_SYMBOL_RE}|${CMP_WORD_RE})\\s*(\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}\\s+${FIELD_RE}\\b`, 'gi');
  rest = rest.replace(cmpFirst, (m, sym, word, val, unit, field) => {
    const cmp = comparatorType(sym, word);
    const mm = parseFloat(val) * unitToMm(unit);
    constraints.push(toConstraint(normalizeField(field), mm, cmp, unit, val));
    return blank(m);
  });

  // ── number+unit, then field, no comparator ("90cm girth") ─────────────────
  const numFirst = new RegExp(`\\b(\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}\\s+${FIELD_RE}\\b`, 'gi');
  rest = rest.replace(numFirst, (m, val, unit, field) => {
    constraints.push(toConstraint(normalizeField(field), parseFloat(val) * unitToMm(unit), 'eq', unit, val));
    return blank(m);
  });

  // ── positional shorthand WITH units — "4in x 3in" (2 nums = Round girth×length) or
  //    "4in x 3in x 7ft" (3 nums = Square width×thickness×length), exact, never assumed. ──────
  const shorthandWithUnits = new RegExp(
    `\\b(\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}\\s*(?:[x×*]|by)\\s*(\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}` +
    `(?:\\s*(?:[x×*]|by)\\s*(\\d+(?:\\.\\d+)?)\\s*${UNIT_RE})?\\b`, 'i');
  // "4x6xX" — a lone "x"/"X" standing in a SLOT position (straight after a separator, with
  // nothing word-like after it) means the same as "?", so it's rewritten to "?" and handled by
  // the single wildcard path below rather than a second parallel grammar. The (?![\w?]) tail is
  // what stops this ever eating a real separator: in "4x3x6" the character after each "x" is a
  // digit, so nothing matches, and "4xx3" is left alone too. Same length in, same length out, so
  // the index/blanking arithmetic below stays correct.
  rest = rest.replace(/([x×*]|by)(\s*)x(?![\w?])/gi, `$1$2${WILDCARD}`);

  // A "?" now means the user deliberately left that slot open ("4x3x?" = any length). Neither
  // matcher above/below can express that — and worse, the units one would happily match just the
  // "4in x 3in" prefix of "4in x 3in x ?" and silently report it as a Round girth×length. So when
  // a wildcard is present, both are skipped in favour of the wildcard-aware matcher further down,
  // which handles units and bare numbers in one pass. With no wildcard typed, nothing changes.
  const hasWildcardSlot = rest.includes(WILDCARD);
  const shWithUnits = hasWildcardSlot ? null : rest.match(shorthandWithUnits);
  if (shWithUnits) {
    const n1 = parseFloat(shWithUnits[1]) * unitToMm(shWithUnits[2]);
    const n2 = parseFloat(shWithUnits[3]) * unitToMm(shWithUnits[4]);
    if (shWithUnits[5] !== undefined) {
      const n3 = parseFloat(shWithUnits[5]) * unitToMm(shWithUnits[6]);
      constraints.push({ field: 'width', minMm: n1, maxMm: n1, assumed: false, label: `width ${shWithUnits[1]}${shWithUnits[2]}` });
      constraints.push({ field: 'thickness', minMm: n2, maxMm: n2, assumed: false, label: `thickness ${shWithUnits[3]}${shWithUnits[4]}` });
      constraints.push({ field: 'length', minMm: n3, maxMm: n3, assumed: false, label: `length ${shWithUnits[5]}${shWithUnits[6]}` });
    } else if (typeHint === false) {
      // Explicit "square" keyword + 2 numbers = width × thickness, length left open — matches
      // Quick Tally's own 2-dim-entry convention (a Square line with no length yet).
      constraints.push({ field: 'width', minMm: n1, maxMm: n1, assumed: false, label: `width ${shWithUnits[1]}${shWithUnits[2]}` });
      constraints.push({ field: 'thickness', minMm: n2, maxMm: n2, assumed: false, label: `thickness ${shWithUnits[3]}${shWithUnits[4]}` });
    } else {
      constraints.push({ field: 'girth', minMm: n1, maxMm: n1, assumed: false, label: `girth ${shWithUnits[1]}${shWithUnits[2]}` });
      constraints.push({ field: 'length', minMm: n2, maxMm: n2, assumed: false, label: `length ${shWithUnits[3]}${shWithUnits[4]}` });
    }
    rest = rest.slice(0, shWithUnits.index) + blank(shWithUnits[0]) + rest.slice((shWithUnits.index || 0) + shWithUnits[0].length);
  } else {
    // ── positional shorthand WITHOUT units — "4x3x6", "90x230" — the everyday case. Assumes
    //    the tenant's own configured default UoM (Application Settings → Tally Defaults),
    //    falling back to today's literal only if that hasn't loaded/been set. Always flagged
    //    assumed=true so the chip shows it was guessed, never silently. ────────────────────────
    const shorthandBare = new RegExp(
      `\\b(\\d+(?:\\.\\d+)?)\\s*(?:[x×*]|by)\\s*(\\d+(?:\\.\\d+)?)(?:\\s*(?:[x×*]|by)\\s*(\\d+(?:\\.\\d+)?))?\\b`, 'i');
    const shBare = rest.match(shorthandBare);
    if (shBare) {
      const n1 = parseFloat(shBare[1]);
      const n2 = parseFloat(shBare[2]);
      if (shBare[3] !== undefined) {
        const n3 = parseFloat(shBare[3]);
        const wUnit = tenantUnitLabel(tenantDefaults?.widthUom, 'Inch');
        const tUnit = tenantUnitLabel(tenantDefaults?.thickUom, 'Inch');
        const lUnit = tenantUnitLabel(tenantDefaults?.squareLengthUom, 'Ft');
        const wMm = n1 * tenantUnitToMm(tenantDefaults?.widthUom, 'Inch');
        const tMm = n2 * tenantUnitToMm(tenantDefaults?.thickUom, 'Inch');
        const lMm = n3 * tenantUnitToMm(tenantDefaults?.squareLengthUom, 'Ft');
        constraints.push({ field: 'width', minMm: wMm, maxMm: wMm, assumed: true, label: `width ${n1}${wUnit} ?` });
        constraints.push({ field: 'thickness', minMm: tMm, maxMm: tMm, assumed: true, label: `thickness ${n2}${tUnit} ?` });
        constraints.push({ field: 'length', minMm: lMm, maxMm: lMm, assumed: true, label: `length ${n3}${lUnit} ?` });
      } else if (typeHint === false) {
        // Explicit "square" keyword + 2 bare numbers = width × thickness, length left open —
        // same Quick Tally 2-dim-entry convention as the with-units branch above.
        const wUnit = tenantUnitLabel(tenantDefaults?.widthUom, 'Inch');
        const tUnit = tenantUnitLabel(tenantDefaults?.thickUom, 'Inch');
        const wMm = n1 * tenantUnitToMm(tenantDefaults?.widthUom, 'Inch');
        const tMm = n2 * tenantUnitToMm(tenantDefaults?.thickUom, 'Inch');
        constraints.push({ field: 'width', minMm: wMm, maxMm: wMm, assumed: true, label: `width ${n1}${wUnit} ?` });
        constraints.push({ field: 'thickness', minMm: tMm, maxMm: tMm, assumed: true, label: `thickness ${n2}${tUnit} ?` });
      } else {
        const gUnit = tenantUnitLabel(tenantDefaults?.girthUom, 'CM');
        const lUnit = tenantUnitLabel(tenantDefaults?.roundLengthUom, 'CM');
        const gMm = n1 * tenantUnitToMm(tenantDefaults?.girthUom, 'CM');
        const lMm = n2 * tenantUnitToMm(tenantDefaults?.roundLengthUom, 'CM');
        constraints.push({ field: 'girth', minMm: gMm, maxMm: gMm, assumed: true, label: `girth ${n1}${gUnit} ?` });
        constraints.push({ field: 'length', minMm: lMm, maxMm: lMm, assumed: true, label: `length ${n2}${lUnit} ?` });
      }
      rest = rest.slice(0, shBare.index) + blank(shBare[0]) + rest.slice((shBare.index || 0) + shBare[0].length);
    }
  }

  // ── positional shorthand WITH a "?" wildcard — "4x3x?", "4x?x6", "90x?", "4in x 3in x ?" ────
  //    Only runs when a "?" is actually in the text, so the two branches above keep their exact
  //    original behaviour for every query that doesn't use one.
  //
  //    Why "?" and not "*" or "X": "*" is ALREADY a separator alias in this grammar (the
  //    separator class is [x×*], so "4*3" means 4 by 3), and "x"/"X" is the separator itself —
  //    neither can double as the wildcard without making "4x6xX" genuinely undecidable. Simply
  //    dropping the last number can't mean it either: "4x3" is already taken, and means Round
  //    girth×length. "?" collides with nothing in the grammar and reads as "anything" already.
  //
  //    A wildcard slot pushes NO constraint — that is the whole point, an open slot must not
  //    filter — it's recorded in `wildcards` only so the chip row can show "length: any".
  //    Each slot takes its own optional unit, so an explicit unit stays exact (assumed=false)
  //    and a bare number falls back to the tenant's default UoM (assumed=true), exactly as the
  //    two branches above already do.
  if (hasWildcardSlot) {
    const slot = `(\\?|\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}?`;
    const sep = `(?:[x×*]|by)`;
    // (?<!\w) / (?!\w) instead of \b — "?" is not a word character, so a plain \b can never sit
    // next to a wildcard slot and the whole pattern would silently never match.
    const wildcardShorthand = new RegExp(
      `(?<!\\w)${slot}\\s*${sep}\\s*${slot}(?:\\s*${sep}\\s*${slot})?(?!\\w)`, 'i');
    const shWild = rest.match(wildcardShorthand);
    // "?x?" / "?x?x?" constrains nothing at all — it would quietly return the entire stock list
    // dressed up as a size search. At least one real number is required for this to count.
    const anyRealNumber = shWild ? [shWild[1], shWild[3], shWild[5]].some(v => v !== undefined && v !== WILDCARD) : false;
    if (shWild && anyRealNumber) {
      const pushSlot = (field: DimensionField, rawValue: string | undefined, explicitUnit: string | undefined,
                        defaultUom: string | null | undefined, fallbackUnit: string) => {
        if (rawValue === undefined) return;
        if (rawValue === WILDCARD) { wildcards.push(field); return; }
        if (explicitUnit) {
          const mm = parseFloat(rawValue) * unitToMm(explicitUnit);
          constraints.push({ field, minMm: mm, maxMm: mm, assumed: false, label: `${field} ${rawValue}${explicitUnit}` });
          return;
        }
        const n = parseFloat(rawValue);
        const unitLabel = tenantUnitLabel(defaultUom, fallbackUnit);
        const mm = n * tenantUnitToMm(defaultUom, fallbackUnit);
        constraints.push({ field, minMm: mm, maxMm: mm, assumed: true, label: `${field} ${n}${unitLabel} ?` });
      };

      if (shWild[5] !== undefined) {
        // 3 slots = Square, always — the same rule the non-wildcard branches already follow.
        // Flagged explicitly because with a wildcard the caller can no longer infer the type
        // from which constraints exist ("?x?x6" leaves neither width nor thickness behind).
        squareShorthand = true;
        pushSlot('width', shWild[1], shWild[2], tenantDefaults?.widthUom, 'Inch');
        pushSlot('thickness', shWild[3], shWild[4], tenantDefaults?.thickUom, 'Inch');
        pushSlot('length', shWild[5], shWild[6], tenantDefaults?.squareLengthUom, 'Ft');
      } else if (typeHint === false) {
        pushSlot('width', shWild[1], shWild[2], tenantDefaults?.widthUom, 'Inch');
        pushSlot('thickness', shWild[3], shWild[4], tenantDefaults?.thickUom, 'Inch');
      } else {
        pushSlot('girth', shWild[1], shWild[2], tenantDefaults?.girthUom, 'CM');
        pushSlot('length', shWild[3], shWild[4], tenantDefaults?.roundLengthUom, 'CM');
      }
      rest = rest.slice(0, shWild.index) + blank(shWild[0]) + rest.slice((shWild.index || 0) + shWild[0].length);
    }
  }

  // ── a single bare number (no unit, no pairing) + an explicit type keyword — "90 round" means
  //    girth ≈90(tenant's round unit); "90 square" means width ≈90(tenant's square unit). Only
  //    fires when the keyword was actually typed — a lone bare number with no keyword stays
  //    unrecognized, unchanged (too ambiguous to guess a field for on its own). ──────────────────
  if (typeHint !== null) {
    const bareSingleRe = /\b(\d+(?:\.\d+)?)\b/;
    const bareSingle = rest.match(bareSingleRe);
    if (bareSingle) {
      const n = parseFloat(bareSingle[1]);
      if (typeHint === true) {
        const gUnit = tenantUnitLabel(tenantDefaults?.girthUom, 'CM');
        const gMm = n * tenantUnitToMm(tenantDefaults?.girthUom, 'CM');
        constraints.push({ field: 'girth', minMm: gMm, maxMm: gMm, assumed: true, label: `girth ${n}${gUnit} ?` });
      } else {
        const wUnit = tenantUnitLabel(tenantDefaults?.widthUom, 'Inch');
        const wMm = n * tenantUnitToMm(tenantDefaults?.widthUom, 'Inch');
        constraints.push({ field: 'width', minMm: wMm, maxMm: wMm, assumed: true, label: `width ${n}${wUnit} ?` });
      }
      rest = rest.slice(0, bareSingle.index) + blank(bareSingle[0]) + rest.slice((bareSingle.index || 0) + bareSingle[0].length);
    }
  }

  // ── whatever number+unit is left over has no field word attached — real, not dropped. ─────
  const ambiguous: AmbiguousSize[] = [];
  const ambiguousRe = new RegExp(`\\b(\\d+(?:\\.\\d+)?)\\s*${UNIT_RE}\\b`, 'gi');
  rest = rest.replace(ambiguousRe, (m, val, unit) => {
    ambiguous.push({ valueMm: parseFloat(val) * unitToMm(unit), rawValue: val, unitLabel: unit });
    return blank(m);
  });

  return { constraints, wildcards, squareShorthand, ambiguous, rest };
}

/** Shared species resolution — exact match, then a strong-confidence auto-pick, then a
 *  plausible-but-uncertain "Did you mean?" list. Used by both the deterministic parser (per
 *  leftover word) and resolveAiGuess() (on the AI's whole species guess) so the two paths can
 *  never drift apart — the "tick" → "PRD Quick 1" fix (see SPECIES_STRONG_THRESHOLD) protects
 *  both equally. Mutates `query` directly, matching how the rest of both callers already work;
 *  returns true when the word was handled in some way (matched or flagged ambiguous), so the
 *  caller knows whether to also fall through to its own "unrecognized" handling. */
function resolveSpecies(word: string, productIndex: FuzzyIndex, products: QuickSearchProduct[], query: QuickSearchQuery): boolean {
  if (query.productId) return true;
  const exact = productIndex.exactMatch(word);
  if (exact) {
    query.productId = Number(exact.value);
    query.productName = exact.label;
    const matchedProduct = products.find(p => p.id === query.productId);
    query.productIsRound = matchedProduct ? matchedProduct.isRound : null;
    query.matches.push({ field: 'species', label: exact.label, fuzzy: false });
    return true;
  }
  if (normalize(word).length < 3) return false;
  const candidates = productIndex.bestMatchesWithScore(word, 3);
  const top = candidates[0];
  if (top && top.score <= SPECIES_STRONG_THRESHOLD) {
    query.productId = Number(top.value);
    query.productName = top.label;
    const matchedProduct = products.find(p => p.id === query.productId);
    query.productIsRound = matchedProduct ? matchedProduct.isRound : null;
    query.matches.push({ field: 'species', label: top.label, fuzzy: true });
    return true;
  }
  if (candidates.length) {
    query.ambiguousSpecies.push({ word, candidates: candidates.map(c => ({ id: Number(c.value), name: c.label })) });
    query.matches.push({ field: 'species-ambiguous', label: word, fuzzy: true });
    return true;
  }
  return false;
}

/** This tenant's configured default unit for a field, applied whenever a caller has a value but
 *  no stated unit — the deterministic parser's own bare-shorthand path, and now the AI path when
 *  the model recognized a field but the text stated no unit for it. isRoundContext picks which of
 *  the two "length" defaults applies (Round's own roundLengthUom vs Square's squareLengthUom) —
 *  same signal both callers already derive from whether a girth field is present anywhere. */
function defaultUnitFor(field: DimensionField, isRoundContext: boolean, tenantDefaults?: QuickSearchTenantDefaults | null): { mmPerUnit: number; unitLabel: string } {
  switch (field) {
    case 'width':
      return { mmPerUnit: tenantUnitToMm(tenantDefaults?.widthUom, 'Inch'), unitLabel: tenantUnitLabel(tenantDefaults?.widthUom, 'Inch') };
    case 'thickness':
      return { mmPerUnit: tenantUnitToMm(tenantDefaults?.thickUom, 'Inch'), unitLabel: tenantUnitLabel(tenantDefaults?.thickUom, 'Inch') };
    case 'girth':
      return { mmPerUnit: tenantUnitToMm(tenantDefaults?.girthUom, 'CM'), unitLabel: tenantUnitLabel(tenantDefaults?.girthUom, 'CM') };
    case 'length':
      return isRoundContext
        ? { mmPerUnit: tenantUnitToMm(tenantDefaults?.roundLengthUom, 'CM'), unitLabel: tenantUnitLabel(tenantDefaults?.roundLengthUom, 'CM') }
        : { mmPerUnit: tenantUnitToMm(tenantDefaults?.squareLengthUom, 'Ft'), unitLabel: tenantUnitLabel(tenantDefaults?.squareLengthUom, 'Ft') };
  }
}

export function parseQuickSearch(
  text: string,
  countries: QuickSearchCountry[],
  grades: QuickSearchGrade[],
  products: QuickSearchProduct[],
  tenantDefaults?: QuickSearchTenantDefaults | null,
): QuickSearchQuery {
  const raw = (text || '').trim();
  const query: QuickSearchQuery = { raw, isRound: null, dimensionConstraints: [], wildcardDimensions: [], ambiguousSizes: [], ambiguousSpecies: [], matches: [] };
  if (!raw) return query;

  const typeHint = extractTypeHint(raw);
  if (typeHint.isRound !== null) {
    query.isRound = typeHint.isRound;
    query.matches.push({ field: 'type', label: typeHint.label!, fuzzy: !typeHint.exact });
  }

  const { constraints, wildcards, squareShorthand, ambiguous, rest: dimsRemoved } =
    extractDimensionConstraints(typeHint.rest, tenantDefaults, typeHint.isRound);
  query.dimensionConstraints = constraints;
  query.wildcardDimensions = wildcards;
  query.ambiguousSizes = ambiguous;
  // An explicit field word ("girth 90cm") is a stronger signal than the type keyword and wins if
  // they ever disagree; when no field constraint exists at all (e.g. a still-ambiguous lone size
  // like "200mm square"), the keyword's isRound set above is left standing.
  if (constraints.some(c => c.field === 'girth')) query.isRound = true;
  else if (constraints.some(c => c.field === 'width' || c.field === 'thickness')) query.isRound = false;
  // A 3-slot shorthand is Square by definition, but with a wildcard the line above can no longer
  // see that — "?x?x6" leaves only a length constraint behind, which on its own says nothing
  // about the type. The shorthand itself is the signal, so it's carried out explicitly.
  else if (squareShorthand) query.isRound = false;
  for (const c of constraints) query.matches.push({ field: 'dimensions', label: c.label, fuzzy: c.assumed });
  // "any" is exactly what was asked for, not a guess — never flagged fuzzy.
  for (const f of wildcards) query.matches.push({ field: 'dimensions', label: `${f}: any`, fuzzy: false });
  for (const a of ambiguous) query.matches.push({ field: 'dimensions', label: `≈${a.rawValue}${a.unitLabel} ?`, fuzzy: true });

  const productIndex = new FuzzyIndex(products.map(p => ({ label: p.name, value: String(p.id) } as FuzzyCandidate)));

  const words = dimsRemoved.split(/\s+/).map(w => w.replace(/[^\p{L}\p{N}]/gu, '')).filter(w => w.length > 0);

  // 2026-09-15: a country name made of several words ("costa rica", "papua new guinea") never
  // matched, because the loop below checks one word at a time. Try whole phrases first (exact,
  // case/punctuation-insensitive — no fuzzy guessing across words), longest first, and take the
  // matched words out so they aren't also flagged as unrecognized.
  const normalizeName = (v: string) => v.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  const multiWordCountries = countries
    .map(c => ({ country: c, size: c.name.trim().split(/\s+/).length, key: normalizeName(c.name) }))
    .filter(c => c.size > 1);
  if (multiWordCountries.length) {
    phraseSearch:
    for (let size = 4; size >= 2; size--) {
      for (let i = 0; i + size <= words.length; i++) {
        const phraseKey = normalizeName(words.slice(i, i + size).join(''));
        const hit = multiWordCountries.find(c => c.size === size && c.key === phraseKey);
        if (hit) {
          query.originCode = hit.country.countryCode;
          query.originName = hit.country.name;
          query.matches.push({ field: 'origin', label: hit.country.name, fuzzy: false });
          words.splice(i, size);
          break phraseSearch;
        }
      }
    }
  }

  for (const word of words) {
    if (!word) continue;
    if (STOPWORDS.has(word.toLowerCase())) continue;

    if (!query.qualityCode) {
      const grade = fuzzyFind(word, grades, g => [g.code, g.label]);
      if (grade) {
        query.qualityCode = grade.item.code;
        query.qualityLabel = grade.item.label;
        query.matches.push({ field: 'quality', label: grade.item.label, fuzzy: !grade.exact });
        continue;
      }
    }
    if (!query.originCode) {
      const country = fuzzyFind(word, countries, c => [c.name, c.countryCode]);
      if (country) {
        query.originCode = country.item.countryCode;
        query.originName = country.item.name;
        query.matches.push({ field: 'origin', label: country.item.name, fuzzy: !country.exact });
        continue;
      }
    }
    if (resolveSpecies(word, productIndex, products, query)) continue;
    // Genuinely unrecognized — flagged, not silently dropped, so a typo/unmatched term is
    // visibly distinguishable from a correctly-understood-but-zero-stock search.
    query.matches.push({ field: 'unrecognized', label: word, fuzzy: false });
  }

  return query;
}

/** AI raw guess shape — matches the backend's AiQuickSearchResultDto field-for-field. The AI's
 *  job stops at "here's what the text seems to say"; resolving those words against real
 *  countries/grades/products still goes through the exact same fuzzy pipeline a typed query
 *  uses below — the AI is never trusted to have picked a real productId/originCode itself.
 *  Dimension constraints mirror the deterministic parser's own shape/units grammar — field +
 *  comparator + value, with unit only when the text actually stated one. */
export interface AiDimensionGuess {
  field: DimensionField;
  comparator: 'eq' | 'gt' | 'gte' | 'lt' | 'lte' | 'between';
  value: number;
  valueMax: number | null;
  /** null when the AI recognized the field but the text stated no unit — resolveAiGuess() then
   *  applies this tenant's own configured default for that field, flagged assumed (same "?"
   *  convention the deterministic parser's bare-shorthand path already uses). */
  unit: 'cm' | 'mm' | 'in' | 'ft' | null;
}

export interface AiQuickSearchGuess {
  speciesGuess: string | null;
  originGuess: string | null;
  qualityGuess: string | null;
  dimensions: AiDimensionGuess[];
}

export function resolveAiGuess(
  raw: string,
  guess: AiQuickSearchGuess,
  countries: QuickSearchCountry[],
  grades: QuickSearchGrade[],
  products: QuickSearchProduct[],
  tenantDefaults?: QuickSearchTenantDefaults | null,
): QuickSearchQuery {
  // The AI path has no wildcard concept of its own — the model returns concrete dimensions or
  // omits them entirely, so this stays empty by design.
  const query: QuickSearchQuery = { raw, isRound: null, dimensionConstraints: [], wildcardDimensions: [], ambiguousSizes: [], ambiguousSpecies: [], matches: [] };

  // Same "hasGirth" signal the deterministic parser's own bare-shorthand path uses to pick which
  // of the two length defaults (Round vs Square) applies to a unit-less "length" entry.
  const hasGirth = (guess.dimensions || []).some(d => d.field === 'girth');

  for (const d of guess.dimensions || []) {
    let mmPerUnit: number;
    let unitLabel: string;
    let assumed: boolean;
    if (d.unit != null) {
      mmPerUnit = MM_PER_UNIT[d.unit] ?? 1;
      unitLabel = d.unit;
      assumed = false;
    } else {
      const fallback = defaultUnitFor(d.field, hasGirth, tenantDefaults);
      mmPerUnit = fallback.mmPerUnit;
      unitLabel = fallback.unitLabel;
      assumed = true;
    }

    const valueMm = d.value * mmPerUnit;
    let constraint: DimensionConstraint;
    if (d.comparator === 'between' && d.valueMax != null) {
      const maxMm = d.valueMax * mmPerUnit;
      constraint = {
        field: d.field, minMm: Math.min(valueMm, maxMm), maxMm: Math.max(valueMm, maxMm), assumed,
        label: `${d.field} ${d.value}-${d.valueMax}${unitLabel}${assumed ? ' ?' : ''}`,
      };
    } else {
      constraint = toConstraint(d.field, valueMm, d.comparator === 'between' ? 'eq' : d.comparator, unitLabel, String(d.value), assumed);
    }
    query.dimensionConstraints.push(constraint);
    query.matches.push({ field: 'dimensions', label: constraint.label, fuzzy: assumed });
  }
  if (query.dimensionConstraints.some(c => c.field === 'girth')) query.isRound = true;
  else if (query.dimensionConstraints.some(c => c.field === 'width' || c.field === 'thickness')) query.isRound = false;

  if (guess.qualityGuess) {
    const grade = fuzzyFind(guess.qualityGuess, grades, g => [g.code, g.label]);
    if (grade) {
      query.qualityCode = grade.item.code;
      query.qualityLabel = grade.item.label;
      query.matches.push({ field: 'quality', label: grade.item.label, fuzzy: !grade.exact });
    }
  }
  if (guess.originGuess) {
    const country = fuzzyFind(guess.originGuess, countries, c => [c.name, c.countryCode]);
    if (country) {
      query.originCode = country.item.countryCode;
      query.originName = country.item.name;
      query.matches.push({ field: 'origin', label: country.item.name, fuzzy: !country.exact });
    }
  }
  if (guess.speciesGuess) {
    // Same 3-tier resolution as a typed query (exact → strong auto-pick → "Did you mean?") —
    // the AI's species guess used to go straight through a top-1-only match here, meaning the
    // "tick" → "PRD Quick 1" style wrong-guess bug this fixed for typed search was still live on
    // the AI path. speciesGuess is already a clean, translated name (the prompt asks for the
    // common trade term), so it's resolved as one whole string, not word-split.
    const productIndex = new FuzzyIndex(products.map(p => ({ label: p.name, value: String(p.id) } as FuzzyCandidate)));
    resolveSpecies(guess.speciesGuess, productIndex, products, query);
  }

  return query;
}
